import { cn } from 'cn';
import { ListIcon, MenuIcon, SearchIcon } from 'lucide-react';
import { NavLink } from 'react-router';
import { PERSONA_LABEL, actingAsDisplay, personaHome, useSession } from '@/app/session';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useShell } from './context';
import { HOMES } from './PersonaNav';
import { signOut } from './SignOutForm';
import { EnvironmentLines } from './UserMenu';

const HOME_LABEL = { admin: 'Pipeline', qa: 'QA queue', doctor: 'My reports' } as const;

/** Phone only: Queue (the persona home), Search, Menu. 56 px plus the safe area. */
export function BottomBar() {
  const { me } = useSession();
  const { setSearchOpen, setMenuOpen } = useShell();
  const home = personaHome(me?.persona);
  const itemClass = 'flex h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium';
  return (
    <nav aria-label="Portal" data-slot="bottom-bar" className="fixed inset-x-0 bottom-0 z-30 flex border-t border-beam-line bg-beam-paper pb-[env(safe-area-inset-bottom)] md:hidden">
      <NavLink to={home} className={({ isActive }) => cn(itemClass, isActive ? 'text-beam-signature' : 'text-beam-text-muted')}>
        <ListIcon aria-hidden="true" className="size-5" />
        {me ? HOME_LABEL[me.persona] : 'Queue'}
      </NavLink>
      <button type="button" onClick={() => setSearchOpen(true)} className={cn(itemClass, 'text-beam-text-muted')}>
        <SearchIcon aria-hidden="true" className="size-5" />
        Search
      </button>
      <button type="button" onClick={() => setMenuOpen(true)} className={cn(itemClass, 'text-beam-text-muted')}>
        <MenuIcon aria-hidden="true" className="size-5" />
        Menu
      </button>
    </nav>
  );
}

/** The phone menu sheet: the five homes, the account block, Environment, Sign out. */
export function MenuSheet() {
  const { me, requestedActingAs } = useSession();
  const { menuOpen, setMenuOpen } = useShell();
  const acting = actingAsDisplay(me?.actingAsName, me?.actingAs ?? null);
  return (
    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
      <SheetContent side="bottom" className="max-h-[85svh] rounded-t-[20px] shadow-beam-3">
        <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded-full bg-beam-line-strong" />
        <SheetHeader className="pb-0">
          <SheetTitle>Menu</SheetTitle>
          <SheetDescription className="sr-only">Persona homes, account and environment</SheetDescription>
        </SheetHeader>
        <nav aria-label="Persona homes" className="flex flex-col px-2">
          {HOMES.map((h) => (
            <NavLink
              key={h.to}
              to={h.to}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) => cn('flex h-11 items-center justify-between rounded-md px-2 text-base no-underline', isActive ? 'bg-beam-cream-deep font-medium' : 'hover:bg-beam-cream-deep')}
            >
              {({ isActive }) => (
                <>
                  {h.label}
                  {isActive ? <span className="text-xs text-beam-text-muted">current</span> : null}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-beam-line px-4 pt-3 text-sm">
          {me ? (
            <p>
              <span className="font-medium">{me.name}</span>
              <span className="text-beam-text-muted"> {'·'} {PERSONA_LABEL[me.persona]}</span>
            </p>
          ) : null}
          {acting || requestedActingAs ? <p className="text-beam-warning">Acting as {acting ?? requestedActingAs} (this tab)</p> : null}
          <Collapsible className="mt-2">
            <CollapsibleTrigger className="flex h-11 w-full items-center justify-between text-left">Environment</CollapsibleTrigger>
            <CollapsibleContent className="pb-2">
              <EnvironmentLines className="flex flex-col gap-1" />
            </CollapsibleContent>
          </Collapsible>
          <div className="pb-4 pt-1">
            <button type="button" onClick={() => void signOut()} className="flex h-11 w-full items-center text-left text-beam-signature">Sign out</button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
